import type { RadioChangeEvent } from "antd";
import { Checkbox, Radio, Space } from "antd";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import to from "await-to-js";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";

import { Button } from "../../../components/Button/Button";
import { ModalsContext } from "../../../context/modals.context";
import { AuthorisedPersonsContext } from "../../../modules/obligee/authorisedPersons/authorisedPersons.context";
import { ExciseStampsContext } from "../../../modules/obligee/exciseStamps/exciseStamps.context";
import { exciseStampsService } from "../../../modules/obligee/exciseStamps/exciseStamps.service";
import { IKategorijeProizvodaProps } from "../../../modules/obligee/exciseStamps/exciseStamps.types";
import { ExciseStampNewRequestForm } from "../../Obligee/ExciseStamp/ExciseStampNewRequestForm/ExciseStampNewRequestForm";
import style from "./ExciseNewRequest.module.scss";

export const ExciseNewRequest = () => {
  const { exciseStamps, setExciseStamps, clearPayloadState } =
    useContext(ExciseStampsContext);
  const { setAuthorisedPersons } = useContext(AuthorisedPersonsContext);
  const { setError } = useContext(ModalsContext);
  const [isCreatedRequest, setIsCreatedRequest] = useState<boolean>(false);

  const radioGroup = () => {
    const handleRadioOptions = () => {
      return exciseStamps.kategorijaProizvoda.map(
        (kategorija: IKategorijeProizvodaProps, index) => {
          return (
            <Radio key={index} value={kategorija.idKategorijeProizvoda}>
              {kategorija.imeKategorijeProizvoda}
            </Radio>
          );
        }
      );
    };
    const onChange = (e: RadioChangeEvent) => {
      setExciseStamps({
        kategorijaProizvodaId: e.target.value,
        tipObveznikaIds: [],
      });
    };
    return (
      <div className={style.radioButtonContainer}>
        <h2>{t("AKCIZNE_MARKICE.TIP_PROIZVODA")}</h2>
        <Radio.Group
          onChange={onChange}
          value={exciseStamps.kategorijaProizvodaId}
        >
          <Space direction="vertical">{handleRadioOptions()}</Space>
        </Radio.Group>
      </div>
    );
  };

  const checkboxGroup = () => {
    const onChange = (checkedValues: CheckboxValueType[]) => {
      setExciseStamps({
        //@ts-ignore
        tipObveznikaIds: checkedValues,
      });
    };
    const options = exciseStampsService.getTipObveznikaOptions(
      exciseStamps.kategorijaProizvodaId,
      exciseStamps.kategorijaProizvoda
    );
    return (
      <>
        {exciseStamps.kategorijaProizvodaId ? (
          <div className={style.checkBoxContainer}>
            <h2>{t("AKCIZNE_MARKICE.TIP_OBVEZNIKA")}</h2>
            <Checkbox.Group
              className={style.checkboxGroup}
              options={options as any}
              onChange={onChange}
              value={exciseStamps.tipObveznikaIds}
            />
          </div>
        ) : null}
      </>
    );
  };

  const getInicijalniPodaci = async () => {
    const [err, res] = await to(
      exciseStampsService.getInicijalniPodaciZaZahtevObveznika()
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setExciseStamps({ kategorijaProizvoda: res.data.kategorijaProizvoda });
  };

  const postVracanjeDodatnihInformacijaZaKreiranjeZahteva = async () => {
    const [err, res] = await to(
      exciseStampsService.postVracanjeDodatnihInformacijaZaKreiranjeZahteva({
        kategorijaProizvodaId: exciseStamps.kategorijaProizvodaId,
        tipObveznikaIds: exciseStamps.tipObveznikaIds,
      })
    );
    if (err || !res.success) return setError(res?.errorMessage);
    setExciseStamps({
      drustvoInformationMarkiceZahtev: res.data.drustvoInformationMarkiceZahtev,
      isUvoznik: res.data.isUvoznik,
      kategorijeZaZahtev: res.data.kategorijeZaZahtev,
      prikaziMaloprodajneCene: res.data.prikaziMaloprodajneCene,
      isPP: res.data.isPP,
    });
    setIsCreatedRequest(res.success);
  };

  useEffect(() => {
    clearPayloadState();
    getInicijalniPodaci();

    return () => {
      clearPayloadState();
    };
  }, []);

  const sendRequest = async () => {
    setExciseStamps({
      kategorijaProizvodaId: exciseStamps.kategorijaProizvodaId,
      tipObveznikaIds: exciseStamps.tipObveznikaIds,
    });
    postVracanjeDodatnihInformacijaZaKreiranjeZahteva();
  };

  const findKategorijaIdName = () => {
    const entitet = exciseStamps.kategorijaProizvoda.find(
      (kategorija) =>
        kategorija.idKategorijeProizvoda === exciseStamps.kategorijaProizvodaId
    );
    const name = entitet?.imeKategorijeProizvoda;
    return name;
  };

  const handleFormType = () => {
    if (
      ["Alkoholna pića", "Алкохолна пића"].includes(
        findKategorijaIdName() as any
      )
    )
      return t("AKCIZNE_MARKICE.ALKOHOL");
    if (["Kafa", "Кафа"].includes(findKategorijaIdName() as any))
      return t("AKCIZNE_MARKICE.KAFA");
    if (
      ["Duvanski proizvodi", "Дувански производи"].includes(
        findKategorijaIdName() as any
      )
    )
      return t("AKCIZNE_MARKICE.DUVAN");
  };

  const isKreirajDisabled = !exciseStamps.tipObveznikaIds.length;

  const ChooseExciseStampRequestType = () => {
    return (
      <div className={style.main}>
        <div className={style.radioGroupContainer}>
          {radioGroup()}
          {checkboxGroup()}
        </div>
        <div>
          <Button
            type="primary"
            onClick={sendRequest}
            className={style.createRequestButton}
            disabled={isKreirajDisabled}
          >
            {t("AKCIZNE_MARKICE.KREIRAJ_ZAHTEV")}
          </Button>
        </div>
      </div>
    );
  };

  return isCreatedRequest ? (
    <ExciseStampNewRequestForm
      title={handleFormType() as any}
      goBack={() => {
        setIsCreatedRequest(false);
        clearPayloadState();
      }}
    />
  ) : (
    <ChooseExciseStampRequestType />
  );
};
